import { useContext, useEffect, useMemo, useState } from 'react'

import {
  OvulationCardEmbed,
  OvulationCardEmbedProps,
  OvulationForm,
  OvulationFormProps,
  OvulationFormValue,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import { ReviewedUserApi } from '@hhgtech/hhg-components/together/interfaces/types'
import dayjs from 'dayjs'

import { callApiNew } from 'api/axios'
import { formatUserProfileUrl } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { DAY_JS_DATE_FORMAT, basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { OvulationContent, OvulationInfoApi } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { OvulationInfoType } from '../ovulation'
import { HealthToolCardEmbedProps } from '../types'

type CommonProps = {
  ovulationPageInfo?: OvulationInfoApi
} & HealthToolCardEmbedProps

export type OvulationCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<OvulationFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<OvulationCardEmbedProps> & CommonProps)

const getOvulationResultLink = (
  baseUrl: string,
  { method, periodDateStart, periodLength, cycle }: OvulationInfoType,
) =>
  `${baseUrl}?${QUERY_HEALTH_TOOL.method}=${method}&${
    QUERY_HEALTH_TOOL.day
  }=${dayjs(periodDateStart).format(DAY_JS_DATE_FORMAT)}&${
    QUERY_HEALTH_TOOL.cycle
  }=${cycle}&${QUERY_HEALTH_TOOL.period}=${periodLength}`

export const OvulationCardWrapper = ({
  onlyUseForm,
  isSide = false,
  showUserResult: _showUserResult,
  ...props
}: OvulationCardWrapperProps) => {
  const { locale, router } = useRouterExtend()

  const { state } = useContext(AppContext) || {}

  const { isLoggedIn } = state || {}
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const toolLink = useMemo(
    () => getHealthToolLink(HEALTH_TOOL_TYPE.OVULATION),
    [getHealthToolLink],
  )

  const updateOvulationInfoResult = (result: OvulationInfoType | null) => {
    if (!!result) {
      const redirectedLink = getOvulationResultLink(
        `${window.location.origin}${basePath}${toolLink}`,
        result,
      )
      router.push(
        getURLwithSSOTracking(redirectedLink, { source: 'ovulation' })?.href ||
          redirectedLink,
      )
    }
  }

  const [ovulationContent, setOvulationContent] = useState<OvulationContent>(
    {} as OvulationContent,
  )

  const expertUrl = useMemo(
    () =>
      formatUserProfileUrl(ovulationContent?.reviewed_by as ReviewedUserApi),
    [ovulationContent],
  )

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(
        PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_OVULATION_INFO,
      )
      setOvulationContent(res?.data as OvulationContent)
    })()
  }, [])

  if (!toolLink)
    return (
      <div
        style={{
          height: 500,
        }}
      ></div>
    )

  const onSubmit = (values: OvulationFormValue) => {
    const info: OvulationInfoType = {
      method: values.calculatorType as any,
      periodDateStart: values.lastPeriodDate as Date,
      cycle: Number(values.cycleLength),
      periodLength: Number(values.periodLength),
      source_url: window.location.href,
    }
    localStorage.setItem('ovulationInfo', JSON.stringify(info))
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(
        getOvulationResultLink(
          `${window.location.origin}${basePath}${toolLink}`,
          info,
        ),
        'ovulation',
      )
    } else {
      updateOvulationInfoResult(info)
    }
  }

  return onlyUseForm ? (
    <OvulationForm
      onSubmit={onSubmit}
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      isMobile={isMobile}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
    />
  ) : (
    <OvulationCardEmbed
      expertUrl={expertUrl}
      ovulationPageInfo={ovulationContent as any}
      formProps={
        {
          isMobile,
          formLayout: isMobile || isSide ? 'default' : 'inline',
          onSubmit,
          submitProps: {
            dataEventLabel: currentUrl,
          },
        } as any
      }
      {...props}
    />
  )
}
